import { Check, ChevronRight } from 'lucide-react';
import { NoRecordIcon } from '../../UI/SvgIcons';
import TableLoader from '../../UI/loaders/TableLoader';

export interface TableColumn<T = unknown> {
  key: string;
  header: string;
  render?: (item: T, index: number) => React.ReactNode;
  className?: string;
  headerClassName?: string;
}

export interface Action<T> {
  text: string;
  onClick: (row: T) => void;
}

export interface TableProps<T = unknown> {
  columns: TableColumn<T>[];
  data: T[];
  showCheckbox?: boolean;
  onSelectAll?: (checked: boolean) => void;
  onSelectItem?: (item: T, checked: boolean, index: number) => void;
  selectedItems?: Set<string | number>;
  getItemId?: (item: T) => string | number;
  className?: string;
  tableClassName?: string;
  minHeight?: string;
  emptyMessage?: string;
  loading?: boolean;
  actions?: Action<T>[];
}

const CommonTable = <T extends Record<string, any>>({
  columns,
  data,
  showCheckbox = false,
  onSelectAll,
  onSelectItem,
  selectedItems = new Set(),
  getItemId = (item) => item.id,
  className = '',
  tableClassName = '',
  minHeight = 'min-h-96',
  emptyMessage = 'No data available',
  loading = false,
  actions,
}: TableProps<T>) => {
  const allSelected =
    data.length > 0 && data.every((item) => selectedItems.has(getItemId(item)));

  const handleSelectAll = (checked: boolean) => {
    onSelectAll?.(checked);
  };
  const handleSelectItem = (item: T, checked: boolean, index: number) => {
    onSelectItem?.(item, checked, index);
  };

  const totalColumns =
    columns.length + (showCheckbox ? 1 : 0) + (actions ? 1 : 0);

  return (
    <div
      className={`overflow-auto flex flex-col justify-between ${minHeight} ${className}`}
    >
      <table className={`w-full text-left ${tableClassName}`}>
        <thead>
          <tr className="*:text-blue-400 *:text-sm *:py-4 *:px-5 *:border-b *:border-gray-200 *:font-medium">
            {showCheckbox && (
              <th>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="w-4 h-4 peer hidden"
                  />
                  <span className="w-5 h-5 rounded border-2 border-light-40 flex items-center justify-center peer-checked:bg-primary peer-checked:border-primary">
                    <Check color="#fff" />
                  </span>
                </label>
              </th>
            )}
            {columns.map((column, index) => (
              <th
                key={`${column.key}-${index}`}
                className={column.headerClassName || ''}
              >
                {column.header}
              </th>
            ))}
            {actions && <th className="cell">Actions</th>}
          </tr>
        </thead>
        <tbody className="divide-y">
          {loading ? (
            <TableLoader columns={totalColumns} rows={totalColumns} />
          ) : data.length === 0 ? (
            <tr>
              <td
                colSpan={totalColumns}
                className="text-center py-8 text-gray-500"
              >
                <div className="flex flex-col justify-center items-center my-10">
                  <NoRecordIcon />
                  <span className="mt-2">{emptyMessage}</span>
                </div>
              </td>
            </tr>
          ) : (
            data.map((item, index) => {
              const itemId = getItemId(item);
              const isSelected = selectedItems.has(itemId);
              return (
                <tr
                  data-id={itemId}
                  key={itemId}
                  className={`text-sm *:border-b *:border-gray-200 *:py-4 *:px-5 *:text-sm *:font-normal`}
                >
                  {showCheckbox && (
                    <td>
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) =>
                            handleSelectItem(item, e.target.checked, index)
                          }
                          className="w-4 h-4 peer hidden"
                        />
                        <span className="w-5 h-5 rounded border-2 border-light-40 flex items-center justify-center peer-checked:bg-primary peer-checked:border-primary">
                          <Check color="#fff" />
                        </span>
                      </label>
                    </td>
                  )}
                  {columns.map((column, index) => (
                    <td
                      key={`${itemId}-${column.key}-${index}`}
                      className={column.className || ''}
                    >
                      {column.render
                        ? column.render(item, index)
                        : item[column.key]}
                    </td>
                  ))}
                  {actions && (
                    <td>
                      <button className="uk-inline">
                        <ChevronRight />
                        {actions.length > 0 && (
                          <div
                            className="rounded-lg"
                            data-uk-dropdown="pos: bottom-right"
                          >
                            <ul className="uk-nav uk-dropdown-nav p-2 text-base">
                              {actions.map((action, i) => (
                                <li key={i}>
                                  <button
                                    onClick={() => action.onClick(item)}
                                    className="flex items-center text-black text-sm capitalize gap-1.5 px-3 py-3 w-full rounded-lg hover:bg-primary-light"
                                  >
                                    {action.text}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default CommonTable;
